import { useEffect, useMemo, useState } from 'react';
import AuthPage from './AuthPage';
import LoadingScreen from './LoadingScreen';
import { useAuth } from '../context/AuthContext';
import {
  buildSmsText,
  auditRefForLead,
  canUseFirmAuditAdmin,
  fetchInternalFirmAuditLeads,
  formatAuditMoney,
  recordFirmAuditEvent,
} from '../lib/firmAudit';
import { showToast } from '../lib/toast';

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f7f4ee',
    color: '#101820',
    fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif",
  },
  shell: {
    width: 'min(1120px, calc(100% - 32px))',
    margin: '0 auto',
    padding: '28px 0 54px',
  },
  top: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 18,
    alignItems: 'flex-end',
    marginBottom: 24,
  },
  kicker: {
    color: '#8a661f',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0,
    marginBottom: 8,
  },
  h1: {
    fontFamily: 'Georgia,serif',
    fontSize: 42,
    lineHeight: 1,
    margin: 0,
  },
  search: {
    minHeight: 42,
    width: 'min(360px, 100%)',
    border: '1px solid #d9cfbf',
    background: '#fff',
    padding: '0 13px',
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 'var(--text-input, 16px)',
    outline: 'none',
  },
  table: {
    background: '#fff',
    border: '1px solid #e2d8ca',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(230px, 1fr) 92px 122px 124px 340px',
    gap: 14,
    alignItems: 'center',
    padding: '14px 16px',
    borderBottom: '1px solid #eee5d8',
  },
  head: {
    color: '#67717d',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0,
    background: '#fbfaf7',
  },
  name: {
    fontWeight: 900,
    lineHeight: 1.25,
  },
  muted: {
    color: '#66717d',
    fontSize: 13,
  },
  actions: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  button: {
    minHeight: 38,
    border: '1px solid #101820',
    background: '#101820',
    color: '#fff',
    padding: '0 12px',
    borderRadius: 8,
    fontWeight: 900,
    cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif",
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondary: {
    background: '#fff',
    color: '#101820',
  },
};

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopyToClipboard(text));
  }
  return fallbackCopyToClipboard(text);
}

function fallbackCopyToClipboard(text) {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  el.style.top = '0';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  return Promise.resolve();
}

function auditUrl(firmId) {
  return `${window.location.origin}/audit/${encodeURIComponent(firmId)}`;
}

export default function FirmAuditAdminPage() {
  const { loading, session, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [state, setState] = useState('loading');
  const [query, setQuery] = useState('');
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    if (loading || !session || !canUseFirmAuditAdmin(user?.email)) return;
    let cancelled = false;
    setState('loading');
    fetchInternalFirmAuditLeads(150)
      .then(rows => {
        if (cancelled) return;
        setLeads(rows);
        setState('ready');
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[firm audit admin] load failed', err);
        setState('error');
      });
    return () => { cancelled = true; };
  }, [loading, session?.user?.id, user?.email]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(lead => String(lead.name || '').toLowerCase().includes(q));
  }, [leads, query]);
  const compact = viewportWidth < 760;
  const rowStyle = compact
    ? {
        ...styles.row,
        gridTemplateColumns: '1fr 92px',
        gap: 10,
        alignItems: 'start',
      }
    : styles.row;
  const actionStyle = compact
    ? {
        ...styles.actions,
        gridColumn: '1 / -1',
        justifyContent: 'stretch',
        flexWrap: 'wrap',
      }
    : styles.actions;
  const compactButtonStyle = compact
    ? { ...styles.button, flex: '1 1 130px' }
    : styles.button;

  if (loading) return <LoadingScreen />;
  if (!session) return <AuthPage />;
  if (!canUseFirmAuditAdmin(user?.email)) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.kicker}>SiteScope internal</div>
          <h1 style={styles.h1}>Access restricted</h1>
          <p style={styles.muted}>Elton's 1 Click Flyer is only available to internal SiteScope accounts.</p>
        </div>
      </div>
    );
  }

  const handleCopySms = async (lead) => {
    const sms = buildSmsText(lead, window.location.origin);
    await copyToClipboard(sms);
    await recordFirmAuditEvent(lead.id, 'copy_sms', { source: 'firm_audit_admin' });
    showToast('SMS copied.');
  };

  const handleCopyLink = async (lead) => {
    await copyToClipboard(auditUrl(auditRefForLead(lead)));
    await recordFirmAuditEvent(lead.id, 'open_admin_link', { source: 'firm_audit_admin', action: 'copy_link' });
    showToast('Invite link copied.');
  };

  const handleOpenFlyer = async (lead) => {
    await recordFirmAuditEvent(lead.id, 'open_admin_link', { source: 'firm_audit_admin', action: 'open_flyer' });
  };

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.top}>
          <div>
            <div style={styles.kicker}>SiteScope internal</div>
            <h1 style={styles.h1}>Elton's 1 Click Flyer</h1>
          </div>
          <input
            style={styles.search}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search firms"
            aria-label="Search firms"
          />
        </div>

        {state === 'loading' && <p style={styles.muted}>Loading flyer leads...</p>}
        {state === 'error' && <p style={styles.muted}>Could not load flyer leads.</p>}

        {state === 'ready' && (
          <div style={styles.table}>
            {!compact && (
              <div style={{ ...styles.row, ...styles.head }}>
                <span>Firm</span>
                <span>Active DAs</span>
                <span>Active pipeline</span>
                <span>Latest</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </div>
            )}
            {filtered.map(lead => (
              <div style={rowStyle} key={lead.id}>
                <div>
                  <div style={styles.name}>{lead.name}</div>
                  <div style={styles.muted}>{lead.sample_role || 'observed'} lead</div>
                </div>
                <strong style={compact ? { textAlign: 'right' } : null}>{Number(lead.da_count || 0).toLocaleString('en-AU')} active</strong>
                <strong>{formatAuditMoney(lead.pipeline_value)}</strong>
                <span style={{ ...styles.muted, ...(compact ? { textAlign: 'right' } : null) }}>{lead.latest_lodged_date || 'No date'}</span>
                <div style={actionStyle}>
                  <a
                    href={auditUrl(auditRefForLead(lead))}
                    target="_blank"
                    rel="noreferrer"
                    style={{ ...compactButtonStyle, ...styles.secondary }}
                    onClick={() => handleOpenFlyer(lead)}
                  >
                    Open flyer
                  </a>
                  <button type="button" style={{ ...compactButtonStyle, ...styles.secondary }} onClick={() => handleCopyLink(lead)}>
                    Copy invite link
                  </button>
                  <button type="button" style={compactButtonStyle} onClick={() => handleCopySms(lead)}>
                    Copy SMS
                  </button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 18, color: '#66717d', fontSize: 14 }}>No firms match that search.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
